import React, { Component } from 'react';
import { Paper, withStyles } from '@material-ui/core';
import { NOTIFICATION_PANEL_CLASS } from '../registerNotificationFonts';
import styles from './styles';
import NotificationsOutlinedIcon from '@material-ui/icons/NotificationsOutlined';
import { renderItem } from '../renderers';
import { NOTIFICATIONS_CONTENT } from '../content';
import { NOTIFICATION_PANEL_FILTERS, type NotificationPanelFilter } from '../constants';
import {
  filterNotificationsByChip,
  getNotificationFilterCounts,
  getToBeReviewedDismissOptions,
} from '../utils';
import type { Item, NotificationHandlers } from '../types';

type NotificationPanelProps = {
  classes: Record<string, string>;
  containerStyles?: string;
  data?: Item[];
  handlers: NotificationHandlers;
  /** Bell dropdown only. Toast popups pass false so All / Messages / Pickups are hidden. */
  showFilterChips?: boolean;
};

type NotificationPanelState = {
  activeFilter: NotificationPanelFilter;
};

export class NotificationPanel extends Component<
  NotificationPanelProps,
  NotificationPanelState
> {
  state: NotificationPanelState = {
    activeFilter: NOTIFICATION_PANEL_FILTERS.ALL,
  };

  setFilter = (activeFilter: NotificationPanelFilter) => {
    this.setState({ activeFilter });
  };

  renderFilterChips = () => {
    const { classes, data = [] } = this.props;
    const { activeFilter } = this.state;
    const counts = getNotificationFilterCounts(data);
    const chips = [
      {
        id: NOTIFICATION_PANEL_FILTERS.ALL,
        label: `${NOTIFICATIONS_CONTENT.panel.filters.all} ${counts.all}`,
      },
      {
        id: NOTIFICATION_PANEL_FILTERS.MESSAGES,
        label: `${NOTIFICATIONS_CONTENT.panel.filters.messages} ${counts.messages}`,
      },
      {
        id: NOTIFICATION_PANEL_FILTERS.PICKUPS,
        label: `${NOTIFICATIONS_CONTENT.panel.filters.pickups} ${counts.pickups}`,
      },
    ];

    return (
      <div className={classes.filterRow}>
        {chips.map((chip) => {
          const isActive = activeFilter === chip.id;
          return (
            <button
              key={chip.id}
              type="button"
              className={`${classes.filterChip} ${isActive ? classes.filterChipActive : ''}`}
              onClick={() => this.setFilter(chip.id)}
              aria-pressed={isActive}
              data-automationid={`notification-filter-${chip.id}`}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
    );
  };

  render() {
    const {
      classes,
      containerStyles,
      data = [],
      handlers,
      showFilterChips = true,
    } = this.props;
    const { activeFilter } = this.state;
    const filteredData = showFilterChips
      ? filterNotificationsByChip(data, activeFilter)
      : data;
    const panelHandlers: NotificationHandlers = {
      ...handlers,
      onPickupReviewClick: (item, options) =>
        handlers.onPickupReviewClick?.(item, {
          ...getToBeReviewedDismissOptions(),
          ...options,
        }),
    };

    return (
      <div className={classes.notificationWrapper}>
        <Paper
          className={`${NOTIFICATION_PANEL_CLASS} ${classes.container} ${containerStyles || ''} customScroll`}
        >
          {showFilterChips ? this.renderFilterChips() : null}
          {filteredData.map((item) => (
            <React.Fragment key={item.id}>
              {renderItem({ item, handlers: panelHandlers })}
            </React.Fragment>
          ))}
          {!filteredData.length ? (
            <div className={classes.emptyMsgContainer}>
              <NotificationsOutlinedIcon className={classes.emptyMsgIcon} />
              <div className={classes.emptyMsgText}>{NOTIFICATIONS_CONTENT.panel.empty}</div>
            </div>
          ) : null}
        </Paper>
      </div>
    );
  }
}

export default withStyles(styles as Parameters<typeof withStyles>[0])(NotificationPanel);
